

import React, { useMemo, useState, useEffect } from 'react';
import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  flexRender,
  createColumnHelper,
  getPaginationRowModel,
  getFilteredRowModel,
} from '@tanstack/react-table';
import {
  Box,
  InputAdornment,
  Pagination,
  Switch,
  FormControlLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Grid,
  Alert,
  Snackbar,
  Autocomplete,
} from '@mui/material';
import {
  Person as PersonIcon,
  Email as EmailIcon,
  WorkOutline as WorkIcon,
  Search as SearchIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import Icon from "@mui/material/Icon";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDInput from "components/MDInput";
import MDButton from "components/MDButton";
import { useMaterialUIController } from "context";
import colors from "assets/theme/base/colors";
import { useAuth } from 'auth/Auth';
import {frontendLogger} from 'utils/logger';
import RolePicker from 'components/RolePicker';
import { useTheme } from '@mui/material/styles';

// UserEditModal Component
const UserEditModal = ({ open, onClose, user }) => {
  const {axiosInstance} = useAuth();
  const [controller] = useMaterialUIController();
  const { darkMode } = controller;
  const { grey } = colors;
  
  const [userData, setUserData] = useState(user);
  const [isUpdating, setIsUpdating] = useState(false);
  const [errorMessage, setErrorMessage] = useState(null)
  const [isResettingPassword, setIsResettingPassword] = useState(false);

  const [availableManagers, setAvailableManagers] = useState([]);
  const [isLoadingManagers, setIsLoadingManagers] = useState(false);
  const [roles, setRoles] = useState([]);
  const hasRealRoles = roles.length > 0;
  const [userInitialData, setUserInitialData] = useState(null);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success',
  });

  // Reset error message when modal opens/closes
  useEffect(() => {
    if (open) {
      setErrorMessage('');
      fetchManagers();
      fetchRoles();
    }
  }, [open]);

  const fetchRoles = async () => {
    try {
      const response = await axiosInstance.get('/users/assignable-roles');
      setRoles(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      // Fall back to the legacy dropdown rather than blocking the edit.
      console.error('Error fetching assignable roles:', error);
      setRoles([]);
    }
  };

  // The row data comes from the list endpoint, which carries only the legacy
  // role string. GET /users/{id} is the one that knows the real assignment.
  useEffect(() => {
    if (!open || !user?.id) return;
    let cancelled = false;

    axiosInstance.get(`/users/${user.id}`)
      .then((response) => {
        if (cancelled || !response.data) return;
        const { base_role_id, addon_role_ids } = response.data;
        const assignment = {
          base_role_id: base_role_id ?? null,
          addon_role_ids: addon_role_ids ?? [],
        };
        setUserData((prev) => ({ ...prev, ...assignment }));
        setUserInitialData((prev) => (prev ? { ...prev, ...assignment } : prev));
      })
      .catch((error) => console.error('Error fetching user roles:', error));

    return () => { cancelled = true; };
  }, [open, user?.id]);

  useEffect(() => {
    if (open && user) {
      // Deep clone the user data to avoid reference issues
      const userDataWithNormalizedRole = {
        ...user,
        role: (user.role || 'standard_user').toLowerCase()
      };
      setUserData(userDataWithNormalizedRole);
      setUserInitialData(userDataWithNormalizedRole);
    }
  }, [open, user]);

  useEffect(() => {
    if (open && user) {
        // Deep clone the user data to avoid reference issues
        setUserData({...user});
        setUserInitialData({...user});
    }
}, [open, user]);


  const handleCloseSnackbar = (event, reason) => {
    if (reason === 'clickaway') return;
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  const fetchManagers = async () => {
    setIsLoadingManagers(true);
    try {
      const response = await axiosInstance.get('/users/');
      setAvailableManagers(response.data);
    } catch (error) {
      console.error('Error fetching managers:', error);
      setErrorMessage('Failed to load managers');
    } finally {
      setIsLoadingManagers(false);
    }
  };


  // const handleInputChange = (field) => (event) => {
  //   let value = event.target.value;
    
  //   // Handle manager_id specifically
  //   if (field === 'manager_id') {
  //     value = value === '' ? null : parseInt(value);
  //   }
    
  //   setUserData({ ...userData, [field]: value });
  // };

  const handleInputChange = (field) => (event) => {
    let value = event.target.value;
    
    // Ensure role is always uppercase
    if (field === 'role') {
      value = value.toUpperCase();
    }
    
    // Handle manager_id specifically
    if (field === 'manager_id') {
      value = value === '' ? null : parseInt(value);
    }
    
    setUserData({ ...userData, [field]: value });
  };

  const handleSave = async () => {
    setIsUpdating(true);

    try {
        // Validation checks
        if (!userData.first_name || !userData.first_name.trim()) {
            setSnackbar({
                open: true,
                message: 'First Name is required',
                severity: 'error'
            });
            setIsUpdating(false);
            return;
        }

        if (!userData.last_name || !userData.last_name.trim()) {
            setSnackbar({
                open: true,
                message: 'Last Name is required',
                severity: 'error'
            });
            setIsUpdating(false);
            return;
        }

        // Email validation
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!userData.email || !emailRegex.test(userData.email)) {
            setSnackbar({
                open: true,
                message: 'Please enter a valid email address',
                severity: 'error'
            });
            setIsUpdating(false);
            return;
        }

        // Detailed comparison function
        const hasFieldChanged = (field) => {
            // Normalize values for comparison
            const normalizeValue = (value) => {
                if (value === null || value === undefined) return null;
                return typeof value === 'string' ? value.trim() : value;
            };

            const currentValue = normalizeValue(userData[field]);
            const originalValue = normalizeValue(userInitialData[field]);

            // Special handling for role to ensure uppercase comparison
            if (field === 'role') {
                return (currentValue?.toUpperCase() !== originalValue?.toUpperCase());
            }

            // Add-ons are a set: order is meaningless and a fresh array is not
            // a change, so comparing by reference would send an update on every
            // save and never send one when only the order differed.
            if (field === 'addon_role_ids') {
                const a = [...(currentValue ?? [])].sort().join(',');
                const b = [...(originalValue ?? [])].sort().join(',');
                return a !== b;
            }

            return currentValue !== originalValue;
        };

        // Prepare FormData with only changed fields
        const formData = new FormData();
        const fieldsToCheck = [
            'first_name',
            'last_name',
            'email',
            // `role` only travels on tenants with no roles table; otherwise the
            // backend derives it from base_role_id, and sending both invites
            // them to disagree.
            ...(hasRealRoles ? ['base_role_id', 'addon_role_ids'] : ['role']),
            'department',
            'designation',
            'manager_id'
        ];

        let hasChanges = false;

        fieldsToCheck.forEach(field => {
            if (hasFieldChanged(field)) {
                let value = userData[field];
                
                // Ensure role is lowercase
                  if (field === 'role') {
                    value = value ? value.toLowerCase() : 'standard_user';
                  }

                // Comma-separated rather than repeated keys: formData.append
                // here emits one key per field, which FastAPI's List[int] form
                // parsing cannot read.
                if (field === 'addon_role_ids') {
                    formData.append(field, (value ?? []).join(','));
                    hasChanges = true;
                    return;
                }

                // Only append if value is not null or undefined
                if (value !== null && value !== undefined) {
                    formData.append(field, value);
                    hasChanges = true;
                }
            }
        });

        // If no changes, show message and return
        if (!hasChanges) {
            setSnackbar({
                open: true,
                message: 'No changes detected',
                severity: 'info'
            });
            setIsUpdating(false);
            return;
        }

        // Log FormData contents for debugging
        for (let [key, value] of formData.entries()) {
            console.log(`${key}: ${value}`);
        }

        // Make API call
        const response = await axiosInstance.patch(`/users/${userData.id}`, formData, {
            headers: {
                'Content-Type': 'multipart/form-data',
            },
        });

        // Success handling
        if (response.data) {
            setSnackbar({
                open: true,
                message: 'User updated successfully',
                severity: 'success'
            });
            
            // Close modal and pass updated data
            onClose(response.data);

            // If user creation is handled here, after a successful user creation, add:
            await frontendLogger.logCreate('User', {
              id: response.data.id,
              user: response.data.username,
              email: response.data.email,
              created_by: userData.username,
              timestamp: new Date().toISOString(),
            });
        }
    } catch (error) {
        // Comprehensive error handling
        let errorMessage = 'An error occurred while updating the user';

        if (error.response) {
            console.error('Error response:', error.response);

            // Handle specific error scenarios
            if (error.response.status === 422) {
                // Validation error handling
                const validationErrors = error.response.data.detail;
                if (Array.isArray(validationErrors)) {
                    errorMessage = validationErrors.map(err => err.msg).join(', ');
                } else if (typeof validationErrors === 'object') {
                    errorMessage = Object.values(validationErrors).flat().join(', ');
                }
            } else if (error.response.status === 409) {
                // Conflict error (e.g., duplicate email)
                errorMessage = 'A user with this email already exists';
            } else {
                // Generic server error
                errorMessage = error.response.data.detail || errorMessage;
            }
        } else if (error.request) {
            // Network error
            errorMessage = 'No response received from the server';
        }

        // Show error in snackbar
        setSnackbar({
            open: true,
            message: errorMessage,
            severity: 'error'
        });

        console.error('Error updating user:', error);
    } finally {
        setIsUpdating(false);
    }
};

const handleResetPassword = async () => {
  setIsResettingPassword(true);
  try {
    const response = await axiosInstance.post('/forgot-password/', {
      username: userData.username
    });

    setSnackbar({
      open: true,
      message: 'Password reset email sent successfully',
      severity: 'success'
    });
  } catch (error) {
    console.error('Error resetting password:', error);
    setSnackbar({
      open: true,
      message: error.response?.data?.detail || 'Failed to send reset password email',
      severity: 'error'
    });
  } finally {
    setIsResettingPassword(false);
  }
};


const handleClose = () => {
  setErrorMessage('');
  onClose(); // Call the onClose prop to close the modal
};
  return (
    <>
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: '16px',
          backgroundColor: darkMode ? 'rgba(40, 44, 52, 0.95)' : 'background.paper',
          backdropFilter: 'blur(12px)',
          boxShadow: '0 8px 32px 0 rgba(31, 38, 135, 0.37)',
          border: '1px solid rgba(255, 255, 255, 0.18)',
        }
      }}
    >
      <DialogTitle>
        <MDBox display="flex" alignItems="center" justifyContent="space-between" mb={1}>
          <MDBox>
            <MDTypography variant="h4" fontWeight="medium">
              Edit User Profile
            </MDTypography>
          </MDBox>
          <IconButton 
            onClick={handleClose} 
            sx={{ 
              backgroundColor: grey[100],
              '&:hover': { backgroundColor: grey[200] }
            }}
          >
            <CloseIcon />
          </IconButton>
        </MDBox>
      </DialogTitle>

      <DialogContent>
      {errorMessage && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {errorMessage}
          </Alert>
        )}
        <MDBox py={2}>
          <Grid container spacing={3}>
            {/* Left Column */}
            <Grid item xs={12} md={6}>
              <MDBox display="flex" flexDirection="column" gap={3}>

                {/* // In the Basic Information section of UserEditModal, update the username input: */}
                <MDBox>
                  <MDTypography variant="h6" color="text" mb={2}>
                    Basic Information
                  </MDTypography>
                  <MDBox display="flex" flexDirection="column" gap={2}>
                    <MDInput
                      label="User ID"
                      value={userData.id != null ? String(userData.id) : ''}
                      fullWidth
                      InputProps={{
                        readOnly: true,
                        sx: {
                          backgroundColor: darkMode ? 'rgba(255, 255, 255, 0.05)' : grey[100],
                          '&:hover': {
                            cursor: 'not-allowed'
                          },
                          '& .MuiInputBase-input': {
                            cursor: 'not-allowed',
                            color: darkMode ? grey[400] : grey[700],
                            fontFamily: 'monospace',
                          }
                        }
                      }}
                    />
                    <MDInput
                      label="Username"
                      value={userData.username}
                      fullWidth
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <PersonIcon sx={{ color: grey[600] }} />
                          </InputAdornment>
                        ),
                        readOnly: true,  // Make it read-only
                        sx: {
                          backgroundColor: darkMode ? 'rgba(255, 255, 255, 0.05)' : grey[100],
                          '&:hover': {
                            cursor: 'not-allowed'
                          },
                          '& .MuiInputBase-input': {
                            cursor: 'not-allowed',
                            color: darkMode ? grey[400] : grey[700],
                          }
                        }
                      }}
                    />
                    <MDInput
                      label="Email"
                      value={userData.email}
                      onChange={handleInputChange('email')}
                      fullWidth
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <EmailIcon sx={{ color: grey[600] }} />
                          </InputAdornment>
                        ),
                      }}
                    />
                    <MDButton
                      variant="outlined"
                      color="info"
                      onClick={handleResetPassword}
                      disabled={isResettingPassword}
                      startIcon={<Icon>lock_reset</Icon>}
                    >
                      {isResettingPassword ? 'Sending Reset Email...' : 'Reset Password'}
                    </MDButton>
                  </MDBox>
                </MDBox>

                <MDBox>
                  <MDTypography variant="h6" color="text" mb={2}>
                    Personal Details
                  </MDTypography>
                  <MDBox display="flex" gap={2}>
                    <MDInput
                      label="First Name"
                      value={userData.first_name}
                      onChange={handleInputChange('first_name')}
                      fullWidth
                    />
                    <MDInput
                      label="Last Name"
                      value={userData.last_name}
                      onChange={handleInputChange('last_name')}
                      fullWidth
                    />
                  </MDBox>
                </MDBox>
              </MDBox>
            </Grid>

            {/* Right Column */}
            <Grid item xs={12} md={6}>
              <MDBox display="flex" flexDirection="column" gap={3}>
                <MDBox>
                  <MDTypography variant="h6" color="text" mb={2}>
                    Role & Department
                  </MDTypography>
                  <MDBox display="flex" flexDirection="column" gap={2}>
                    {hasRealRoles ? (
                      <RolePicker
                        roles={roles}
                        baseRoleId={userData.base_role_id ?? null}
                        addonRoleIds={userData.addon_role_ids ?? []}
                        onBaseRoleChange={(id) => setUserData({
                          ...userData,
                          base_role_id: id,
                          addon_role_ids: id ? (userData.addon_role_ids ?? []) : [],
                        })}
                        onAddonRolesChange={(ids) => setUserData({
                          ...userData,
                          addon_role_ids: ids,
                        })}
                      />
                    ) : (
                      <MDInput
                        select
                        label="Role"
                        value={userData.role?.toLowerCase() || 'standard_user'}
                        onChange={(e) => {
                          const newRole = e.target.value.toLowerCase();
                          setUserData({
                            ...userData,
                            role: newRole
                          });
                        }}
                        fullWidth
                        SelectProps={{
                          native: true,
                        }}
                      >
                        <option value="standard_user">User</option>
                        <option value="manager">Manager</option>
                        <option value="system_admin">System Admin</option>
                      </MDInput>
                    )}

                    <MDInput
                      label="Department"
                      value={userData.department || ''}
                      onChange={handleInputChange('department')}
                      fullWidth
                    />
                  </MDBox>
                </MDBox>

                <MDBox>
                  <MDTypography variant="h6" color="text" mb={2}>
                    Management
                  </MDTypography>
                  <MDBox display="flex" flexDirection="column" gap={2}>
                    <Autocomplete
                      fullWidth
                      options={availableManagers.filter(manager => manager.id !== userData.id)}
                      getOptionLabel={(option) => `${option.first_name} ${option.last_name}`}
                      value={
                        availableManagers.find(manager => manager.id === userData.manager_id) || null
                      }
                      onChange={(_, newValue) => {
                        setUserData({
                          ...userData, 
                          manager_id: newValue ? newValue.id : null
                        });
                      }}
                      renderInput={(params) => (
                        <MDInput 
                          {...params} 
                          label="Manager" 
                          fullWidth
                          disabled={isLoadingManagers}
                          sx={{
                            '& .MuiInputBase-root': {
                              height: '45px',
                            },
                            '& .MuiInputLabel-root': {
                              backgroundColor: darkMode ? 'rgba(40, 44, 52, 0.95)' : 'white',
                              paddingX: 1,
                            },
                            '& .MuiInputBase-input': {
                              height: '25px',
                              padding: '0 15px !important',
                            }
                          }}
                        />
                      )}
                      loading={isLoadingManagers}
                      noOptionsText="No managers found"
                      clearText="Clear selection"
                      sx={{
                        '& .MuiAutocomplete-clearIndicator': {
                          color: 'rgba(0,0,0,0.54)',
                        },
                        '& .MuiAutocomplete-input': {
                          height: '25px !important',
                        }
                      }}
                    />
                  </MDBox>
                </MDBox>



              </MDBox>
            </Grid>
          </Grid>
        </MDBox>
      </DialogContent>

      <DialogActions 
        sx={{ 
          p: 3, 
          borderTop: `1px solid ${grey[200]}`,
        }}
      >
        <MDButton
          variant="outlined"
          color="secondary"
          onClick={handleClose}
          disabled={isUpdating}
        >
          Cancel
        </MDButton>
        <MDButton
          variant="gradient"
          color="info"
          onClick={handleSave}
          disabled={isUpdating}
        >
          {isUpdating ? 'Saving...' : 'Save Changes'}
        </MDButton>
      </DialogActions>
    </Dialog>

    <Snackbar
      open={snackbar.open}
      autoHideDuration={6000}
      onClose={handleCloseSnackbar}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert 
          onClose={handleCloseSnackbar} 
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
        {snackbar.message}
      </Alert>
    </Snackbar>
  </>
  );
};

const formatNameInitials = (fullName) => {
  return fullName
    .split(' ')
    .map(name => name.charAt(0).toUpperCase() + name.slice(1).toLowerCase())
    .join(' ');
};

const UserTable = ({
  users,  // Keep this as we need the initial data
  handleToggleUserStatus,  // Keep this as it might be needed at parent level
  onAddUser,  // Keep this as it's controlled by parent
  onUserUpdate, // Add this new prop
  onDownloadLogs, // Add this new prop
  onDownloadAllLogs, // Add this new prop
}) => {
  const isV2 = useTheme()?.pearlUi === 'v2';

  const [controller] = useMaterialUIController();
  const { darkMode } = controller;
  const { info, error, success, grey } = colors;
  
  const [globalFilter, setGlobalFilter] = useState('');
  const [sorting, setSorting] = useState([]);
  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: 10,
  });

  // Modal state
  const [selectedUser, setSelectedUser] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  // Add this state
  const [localUsers, setLocalUsers] = useState(users);

  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success',
  });

    // Update useEffect to sync localUsers with users prop
    useEffect(() => {
      setLocalUsers(users);
    }, [users]);

    const handleUserUpdate = (updatedUser) => {
      // Update the local users state
      setLocalUsers(prevUsers => 
        prevUsers.map(user => 
          user.id === updatedUser.id 
            ? { ...user, ...updatedUser } 
            : user
        )
      );
    };
  
  const handleSnackbarClose = (event, reason) => {
    if (reason === 'clickaway') return;
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  const getFullName = (user) => {
    const nameParts = [
      user.first_name || '',
      user.last_name || ''
    ].filter(part => part.trim() !== '');
    return nameParts.join(' ').trim() || user.username;
  };

  // Update other functions to use localUsers
  const findManagerById = (managerId) => {
    return localUsers.find(u => u.id === managerId) || null;
  };

  const handleUserClick = (userId) => {
    if (!userId) {
      console.error('Invalid user ID');
      return;
    }
    
    const user = localUsers.find(u => u.id === userId);
    if (!user) {
      console.error('User not found');
      return;
    }
    
    setSelectedUser(user);
    setIsModalOpen(true);
  };

const handleCloseModal = (updatedUser) => {
  if (updatedUser) {
    // Trigger parent-level refresh
    if (typeof onUserUpdate === 'function') {
      onUserUpdate(updatedUser);
    }

    // Show success message
    setSnackbar({
      open: true,
      message: 'User updated successfully',
      severity: 'success'
    });
  }
  
  // Reset modal state
  setIsModalOpen(false);
  setSelectedUser(null);
};

  const columnHelper = createColumnHelper();

  const columns = useMemo(() => [
    columnHelper.accessor('id', {
      header: 'User ID',
      cell: info => (
        <MDBox
          onClick={() => handleUserClick(info.row.original.id)}
          sx={{
            cursor: 'pointer',
            '&:hover': { textDecoration: 'underline' },
          }}
        >
          <MDTypography
            variant="caption"
            fontWeight="medium"
            sx={{ fontFamily: 'monospace', color: grey[700] }}
          >
            #{info.getValue()}
          </MDTypography>
        </MDBox>
      ),
    }),
    columnHelper.accessor('username', {
      header: 'Username',
      cell: info => (
        <MDBox 
          onClick={() => handleUserClick(info.row.original.id)}
          sx={{ 
            cursor: 'pointer', 
            display: 'flex', 
            alignItems: 'center',
            justifyContent: 'flex-start',
            gap: 1,
            color: info.main,
            '&:hover': { textDecoration: 'underline' }
          }}
        >
          <PersonIcon fontSize="small" sx={{ color: grey[600] }} />
          <MDTypography variant="caption" fontWeight="regular">
            {info.getValue()}
          </MDTypography>
        </MDBox>
      ),
    }),
    columnHelper.accessor(row => getFullName(row), {
      header: 'Full Name',
      cell: info => (
        <MDBox
          onClick={() => handleUserClick(info.row.original.id)}
          sx={{ 
            cursor: 'pointer',
            '&:hover': { textDecoration: 'underline' }
          }}
        >
          <MDTypography variant="caption" fontWeight="medium">
            {formatNameInitials(info.getValue())}
          </MDTypography>
        </MDBox>
      ),
    }),
    columnHelper.accessor('email', {
      header: 'Email',
      cell: info => (
        <MDBox 
          component="a"
          href={`mailto:${info.getValue()}`}
          sx={{ 
            display: 'flex', 
            alignItems: 'center',
            justifyContent: 'flex-start',
            gap: 1,
            textDecoration: 'none',
            color: 'inherit',
            '&:hover': {
              textDecoration: 'underline'
            }
          }}
        >
          <EmailIcon fontSize="small" sx={{ color: grey[600] }} />
          <MDTypography variant="caption" fontWeight="medium" color="text">
            {info.getValue()}
          </MDTypography>
        </MDBox>
      ),
    }),
    columnHelper.accessor('role', {
      header: 'Role',
      // Named `cellInfo`, not `info`: the bare name shadows the destructured
      // colors.info above, so the manager branch resolved to undefined and
      // those chips rendered with no background at all.
      cell: cellInfo => {
        const role = cellInfo.getValue();
        const row = cellInfo.row.original;
        // Show the role the person actually holds. `role` is the derived legacy
        // enum, which collapses every custom base role to standard_user -- so
        // someone on "Sales Manager" read as STANDARD USER here while the edit
        // dialog showed the real role. Falls back to the enum on tenants that
        // have no roles table.
        const label = row?.base_role_name || (role ? String(role).toUpperCase().replace(/_/g, ' ') : null);
        if (!label) return '-';

        // The backend stores the enum lower case, so the uppercase comparisons
        // this started with never matched and every chip rendered grey.
        const kind = String(role || '').toUpperCase();
        const isAdmin = row?.base_role_is_super ?? (kind === 'SYSTEM_ADMIN');
        const isManager = row?.base_role_name
          ? !row.base_role_is_super && kind === 'MANAGER'
          : kind === 'MANAGER';

        return (
          <MDBox 
            component="span"
            sx={{
              backgroundColor: 
                isAdmin ? error.light : 
                isManager ? info.light : 
                grey[50],
              color: 
                isAdmin ? error.main : 
                isManager ? info.main : 
                grey[700],
              px: 1,
              py: 0.5,
              borderRadius: 1,
              fontSize: '0.75rem',
              fontWeight: 'bold',
              display: 'inline-block'
            }}
          >
            {label}
          </MDBox>
        );
      },
    }),
    columnHelper.accessor('department', {
      header: 'Department',
      cell: info => (
        <MDBox sx={{ 
          display: 'flex', 
          alignItems: 'center',
          justifyContent: 'flex-start',
          gap: 1,
        }}>
          <MDTypography variant="caption" fontWeight="medium">
            {info.getValue() || '-'}
          </MDTypography>
        </MDBox>
      ),
    }),
    columnHelper.accessor('manager_id', {
      header: 'Manager',
      cell: info => {
        const managerId = info.getValue();
        const manager = findManagerById(managerId);
        return (
          <MDBox sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <WorkIcon fontSize="small" sx={{ color: grey[600] }} />
            <MDTypography variant="caption" fontWeight="medium">
              {manager ? formatNameInitials(getFullName(manager)) : '-'}
            </MDTypography>
          </MDBox>
        );
      },
    }),
    columnHelper.accessor('is_active', {
      header: 'Status',
      cell: info => {
        const isActive = info.getValue();
        const userId = info.row.original.id;
        
        return (
          <FormControlLabel
            sx={{ m: 0 }}
            control={
              <Switch
                checked={isActive}
                onChange={() => handleToggleUserStatus(userId, isActive)}
                color="primary"
              />
            }
            label={
              <MDTypography
                variant="caption"
                fontWeight="bold"
                color={isActive ? 'success' : 'error'}
              >
                {isActive ? 'Active' : 'Inactive'}
              </MDTypography>
            }
          />
        );
      },
    }),
    // Add Actions column
    columnHelper.display({
      id: 'actions',
      header: 'Actions',
      cell: info => (
        <MDButton
          variant="outlined"
          color="info"
          size="small"
          onClick={() => onDownloadLogs && onDownloadLogs(info.row.original)}
        >
          Download Logs
        </MDButton>
      ),
    }),
  ], [localUsers, handleToggleUserStatus, getFullName, findManagerById, onDownloadLogs]);

  const table = useReactTable({
    data: localUsers, // Changed from users to localUsers
    columns,
    state: {
      sorting,
      globalFilter,
      pagination,
    },
    onSortingChange: setSorting,
    onGlobalFilterChange: setGlobalFilter,
    onPaginationChange: setPagination,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
  });

  return (
    <MDBox display="flex" flexDirection="column" height="100%">
      <MDBox p={3}>
        <MDBox mb={3} display="flex" gap={2} alignItems="center">
          <MDInput
            fullWidth
            value={globalFilter}
            onChange={e => setGlobalFilter(e.target.value)}
            placeholder="Search users..."
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
          />
          {/* v2: a plain toolbar action instead of the large centred button. */}
          {isV2 && onDownloadAllLogs && (
            <MDButton variant="outlined" color="dark" onClick={onDownloadAllLogs} sx={{ minWidth: 'fit-content' }}>
              Download All User Logs
            </MDButton>
          )}
          {onAddUser && (
            <MDButton 
              variant="gradient" 
              color="info" 
              onClick={onAddUser}
              sx={{ minWidth: 'fit-content' }}
            >
              Add New User
            </MDButton>
          )}
        </MDBox>
        {!isV2 && onDownloadAllLogs && (
          <MDBox display="flex" justifyContent="center" alignItems="center" mb={3}>
            <MDButton
              variant="gradient"
              color="info"
              size="large"
              onClick={onDownloadAllLogs}
              startIcon={<i className="fas fa-download" />}
              sx={{
                fontWeight: 'bold',
                borderRadius: 2,
                px: 3,
                py: 1.5,
                fontSize: '1rem',
                textTransform: 'none',
              }}
            >
              Download All User Logs
            </MDButton>
          </MDBox>
        )}
        <Box sx={{
          width: '100%',
          overflowX: 'auto',
          '& table': {
            width: '100%',
            borderCollapse: 'separate',
            borderSpacing: 0,
          },
          '& th': {
            backgroundColor: darkMode ? 'transparent' : 'background.paper',
            borderBottom: (theme) => `1px solid ${theme.palette.divider}`,
            padding: '12px 16px',
            transition: 'all 300ms linear',
            '&:hover': {
              backgroundColor: darkMode ? 'rgba(255, 255, 255, 0.04)' : 'rgba(0, 0, 0, 0.04)',
            },
          },
          '& td': {
            padding: '12px 16px',
            borderBottom: (theme) => `1px solid ${theme.palette.divider}`,
            verticalAlign: 'middle',
          },
          '& tr:last-child td': {
            borderBottom: 'none',
          },
          '& tr:hover td': {
            backgroundColor: darkMode ? 'rgba(255, 255, 255, 0.02)' : 'rgba(0, 0, 0, 0.02)',
          },
        }}>
          <table>
            <thead>
              {table.getHeaderGroups().map(headerGroup => (
                <tr key={headerGroup.id}>
                  {headerGroup.headers.map(header => (
                    <th key={header.id}>
                      <MDBox
                        onClick={header.column.getToggleSortingHandler()}
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'flex-start',
                          cursor: 'pointer',
                          gap: 1,
                          color: darkMode ? 'white' : 'text.primary',
                          opacity: 0.7,
                          textTransform: 'uppercase',
                          fontSize: '0.75rem',
                          fontWeight: 700,
                        }}
                      >
                        {flexRender(header.column.columnDef.header, header.getContext())}
                        <MDBox sx={{ display: 'flex', flexDirection: 'column', ml: 1 }}>
                          <Icon
                            sx={{
                              color: header.column.getIsSorted() === 'asc' ? 'primary.main' : 'text.secondary',
                              opacity: header.column.getIsSorted() === 'asc' ? 1 : 0.5,
                              fontSize: '1.125rem',
                            }}
                          >
                            arrow_drop_up
                          </Icon>
                          <Icon
                            sx={{
                              color: header.column.getIsSorted() === 'desc' ? 'primary.main' : 'text.secondary',
                              opacity: header.column.getIsSorted() === 'desc' ? 1 : 0.5,
                              fontSize: '1.125rem',
                              mt: -1,
                            }}
                          >
                            arrow_drop_down
                          </Icon>
                        </MDBox>
                      </MDBox>
                    </th>
                  ))}
                </tr>
              ))}
            </thead>
            <tbody>
              {table.getRowModel().rows.map(row => (
                <tr key={row.id}>
                  {row.getVisibleCells().map(cell => (
                    <td key={cell.id}>
                      <MDBox
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'flex-start',
                        }}
                      >
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                      </MDBox>
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </Box>

        <MDBox
          display="flex"
          flexDirection="column"
          alignItems="center"
          p={3}
        >
          <MDTypography variant="button" color="secondary" fontWeight="regular" mb={2}>
            Showing {table.getState().pagination.pageIndex * table.getState().pagination.pageSize + 1} to{' '}
            {Math.min((table.getState().pagination.pageIndex + 1) * table.getState().pagination.pageSize, localUsers.length)} of{' '}
            {localUsers.length} entries
          </MDTypography>
          
          <Pagination
            count={table.getPageCount()}
            page={table.getState().pagination.pageIndex + 1}
            onChange={(_, page) => table.setPageIndex(page - 1)}
            color="primary"
            variant="gradient"
            shape="rounded"
            sx={{
              '& .MuiPaginationItem-root': {
                borderRadius: '50%',
              },
            }}
          />
        </MDBox>
      </MDBox>

      {selectedUser && (
        <UserEditModal
          open={isModalOpen}
          onClose={handleCloseModal}
          user={selectedUser}
        />
      )}

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert 
          onClose={handleSnackbarClose} 
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </MDBox>
  );
};

export default UserTable;